import { ColumnStore, NumberColumn } from "@bryntum/schedulerpro";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import {
  defaultCellRenderer as renderer,
  defaultGroupRenderer as groupRenderer, showArchiveRecordAlert, swivuGridSorterFn
} from "../../other/utils/utils.jsx";
import {
  annotateField,
  budgetColRenderer,
  budgetCurrencyFormat,
  budgetNumberFormat,
  budgetSummaryRenderer,
  DEFAULT_BUDGET_FIELDS,
  isColorBudgetCell
} from "../../other/utils/budgetUtils.jsx";
import SwivuSortColumn from "./SwivuSortColumn.jsx";
import { getSetting } from "../../react/context/bryntumContext.jsx";
import { MIXED_TASK_COLOR, MIXED_TASK_STYLE } from "../../other/constants/TaskTypes.jsx";
import { taskRenderer } from "../../other/utils/bgUtils.jsx";
import { ViewType } from "../../other/constants/OtherKeys.jsx";
import { getIsArchivedForBoard } from "../../other/utils/otherUtils.jsx";
import { getSavedBoardSelection } from "../../react/hooks/useBoards.jsx";

const HasBudgetColumnMixin = (Target) => class extends Target {
  static get $name() {
    return "HasBudgetColumnMixin";
  }

  static get defaults() {
    return {
      minWidth: "7em",
      height: "4em",
      align: "left",
      groupRenderer,
      afterRenderCell: ({ cellElement, column, record }) => annotateField(cellElement, column.field, record),
      sum: "count",
      summaryRenderer: ({ sum }) => `Rows ${sum}`,
    }
  }
}

export class TaskBudgetColumn extends HasBudgetColumnMixin(NumberColumn) {
  static get $name() {
    return "TaskBudgetColumn";
  }

  static get type() {
    return WIDGET_KEYS.columnTaskBudget;
  }

  static get defaults() {
    return {
      renderer(e) {
        if (!e.record.isGroupHeader) {
          return budgetColRenderer(e, false);
        }
      }
    }
  }
}

export class TaskBudgetAmountColumn extends TaskBudgetColumn {
  static get $name() {
    return "TaskBudgetAmountColumn";
  }

  static get type() {
    return WIDGET_KEYS.columnTaskBudgetAmount;
  }

  static get defaults() {
    return {
      ...DEFAULT_BUDGET_FIELDS.amountField,
      editor: DEFAULT_BUDGET_FIELDS.amountField,
      renderer(e) {
        if (!e.record.isGroupHeader) {
          return budgetColRenderer(e, isColorBudgetCell(e.column.field));
        }
      },
      sum: "sum",
      summaryRenderer: budgetSummaryRenderer(budgetCurrencyFormat)
    }
  }
}

export class TaskBudgetPercentageColumn extends TaskBudgetColumn {
  static get $name() {
    return "TaskBudgetPercentageColumn";
  }

  static get type() {
    return WIDGET_KEYS.columnTaskBudgetPercentage;
  }

  static get defaults() {
    return {
      ...DEFAULT_BUDGET_FIELDS.percentageField,
      editor: DEFAULT_BUDGET_FIELDS.percentageField,
      renderer(e) {
        if (!e.record.isGroupHeader) {
          const domConfig = budgetColRenderer(e, isColorBudgetCell(e.column.field));
          domConfig.text = domConfig.text + "%";
          return domConfig
        }
      },
      sum: "average",
      summaryRenderer: budgetSummaryRenderer(budgetNumberFormat, "AVG: ")
    }
  }
}

export class TaskBudgetTextColumn extends HasBudgetColumnMixin(SwivuSortColumn) {
  static get $name() {
    return "TaskBudgetTextColumn";
  }

  static get fields() {
    return [
      { name: "swivuListTitle",type: "string", readOnly: true, defaultValue: "Linked Tasks" },
      { name: "canMultiSelectSwivuList",type: "boolean", readOnly: true, defaultValue: true },
      { name: "swivuListSearchKey",type: "string", readOnly: true, defaultValue: "name" },
      { name: "popupCls",type: "array", readOnly: true, defaultValue: ["swivu-task-list-popup", "minimal-gradient-editor"] },
    ]
  }

  static get type(){
    return WIDGET_KEYS.columnTaskBudgetText
  }

  static get defaults() {
    return {
      renderer(e) {
        if (!e.record.isGroupHeader) {
          return renderer(e);
        }
      }
    }
  }

  onLoad(project) {
    this.project = project;
  }

  getSelectedSwivuListItem(budgetRecord) {
    // Bug: When swivuListItems is being assigned to Element editor list, it resets the relations array, Might be a bryntum bug
    // Therefore, saving budgetTasks to a new array reference
    return [...budgetRecord.budgetTasks];
  }

  get swivuListItems() {
    const selectedBoard = getSavedBoardSelection();
    return this.project.extraTaskStore.query((record) => !record.isLinked && (getIsArchivedForBoard(selectedBoard.slug) || !record.archived)).sort(swivuGridSorterFn.bind({ field: "name" }));
  }

  onSwivuListSelectionChange(budgetRecord, taskRecordsToLink) {
    this.project.beginBatch();
    const taskRecordsToUnlink = budgetRecord.budgetTasks.filter((budgetTask) => !taskRecordsToLink.find((taskToLink) => taskToLink.id === budgetTask.id));
    taskRecordsToUnlink.forEach((taskRecord) => taskRecord.budgetId = null);
    taskRecordsToLink.forEach((taskRecord) => taskRecord.budgetId = budgetRecord.id);
    this.project.endBatch();
    /* Bug: We need to refresh the cell here manually because the cell is not refreshed automatically when a 'related' record is updated (this happens for 'related field' in model), In bryntum 6.03 this is fixed, but the bug persists on higher versions
    Reported Issue: https://forum.bryntum.com/viewtopic.php?t=32966 */
    this.refreshCell(budgetRecord);
  }

  getSwivuListItemDisplayValue(record) {
    const globalTaskColor = getSetting("taskColor", MIXED_TASK_COLOR, true);
    const globalTaskStyle = getSetting("taskStyle", MIXED_TASK_STYLE, true);
    const viewType = getSetting("viewType", ViewType.normal);
    return taskRenderer(record, globalTaskColor, globalTaskStyle, viewType, this.grid?.rowHeight, this.barMargin)
  }

  onSwivuBeforeListSelectionChange(record, taskRecordsToLink) {
    return !(showArchiveRecordAlert(record) || showArchiveRecordAlert(taskRecordsToLink));
  }
}

ColumnStore.registerColumnType(TaskBudgetColumn);
ColumnStore.registerColumnType(TaskBudgetAmountColumn);
ColumnStore.registerColumnType(TaskBudgetPercentageColumn);
ColumnStore.registerColumnType(TaskBudgetTextColumn);
